// デバイス棚卸しデータタブページ
import { FillScreen, HStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import { DeviceGenre } from "@/features/device/genre/type";
import { DeviceInventoryOperationData } from "@/routes/_staff/it-operation-core/device-inventory/-components/data/DeviceInventoryOperationData";
import { listSelfReports } from "@/routes/_staff/it-operation-core/_root/packages/-components/DeviceInventoryOperation/query/api";
import type { ValueOrUpdater } from "@dresscode-frontend/utility-types";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect } from "react";
import { z } from "zod";
import { ErrorDisplay } from "./-components/common";

// 検索パラメータのバリデーションスキーマ
const searchSchema = z
  .object({
    patternId: z.string(),
    requestId: z.string().optional(),
    packageInstanceId: z.string().optional(),
    pageIndex: z.number().int().min(0).optional(),
    pageSize: z.number().int().min(1).max(1000).optional(),
    sortColumn: z.string().optional(),
    sortOrder: z.enum(["asc", "desc"]).optional(),

    // セルフレポート前のフィルター
    freeText: z.string().optional(),
    deviceGenres: z.array(z.nativeEnum(DeviceGenre)).optional(),
    assigned: z.boolean().optional(),

    // セルフレポート後のフィルター
    reportStatus: z.enum(["all", "completed", "notCompleted"]).optional(),
    hasError: z.boolean().optional(),

    // 既存フィルター（後方互換性のため残す）
    statusFilter: z.enum(["all", "good", "bad", "unreported"]).optional(),
    typeFilter: z.enum(["all", "assigned", "installed"]).optional(),
  })
  .refine(
    (data) => data.requestId !== undefined || data.packageInstanceId !== undefined,
    {
      message: "requestId または packageInstanceId のどちらかが必須です",
    },
  );

export const Route = createFileRoute(
  "/_staff/it-operation-core/device-inventory/data",
)({
  validateSearch: zodValidator(searchSchema),
  component: RouteComponent,
});

/**
 * packageInstanceIdからrequestIdを取得するカスタムフック
 *
 * アーカイブページからの遷移時に使用。
 * packageInstanceIdを使ってAPIからrequestIdを取得する。
 *
 * @param packageInstanceId - パッケージインスタンスID
 * @param requestId - 既にrequestIdがある場合はクエリを実行しない
 * @returns requestId、ローディング状態、エラー状態
 */
function usePackageInstanceIdToRequestId(
  packageInstanceId: string | undefined,
  requestId: string | undefined,
): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: ["packageInstanceId-to-requestId", packageInstanceId],
    queryFn: async () => {
      // enabledオプションで存在が保証されているが、型安全性のため明示的にチェック
      if (!packageInstanceId) {
        throw new Error("packageInstanceId is required");
      }
      const response = await listSelfReports({
        packageInstanceId,
        page: 1,
        pageSize: 1,
      });
      const extractedRequestId = response.items[0]?.requestId;
      if (!extractedRequestId) {
        throw new Error("requestId not found in response");
      }
      return extractedRequestId;
    },
    enabled: !!packageInstanceId && !requestId,
    staleTime: 0,
    gcTime: 0,
  });
}

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  // packageInstanceIdからrequestIdを取得
  const {
    data: conversionData,
    isPending: isConverting,
    isError,
  } = usePackageInstanceIdToRequestId(
    searchParams.packageInstanceId,
    searchParams.requestId,
  );

  // requestIdを取得したらURLを更新（packageInstanceIdを削除）
  useEffect(() => {
    if (conversionData && searchParams.packageInstanceId && !searchParams.requestId) {
      navigate({
        search: (prev) => ({
          ...prev,
          requestId: conversionData,
          packageInstanceId: undefined,
        }),
        replace: true,
      });
    }
  }, [conversionData, searchParams.packageInstanceId, searchParams.requestId, navigate]);

  const handleSearch = useCallback(
    (params: ValueOrUpdater<Partial<typeof searchParams>>) => {
      navigate({
        search: (prev) => {
          const newParams = typeof params === "function" ? params(prev) : params;
          return { ...prev, ...newParams };
        },
      });
    },
    [navigate],
  );

  // エラーが発生した場合
  if (isError) {
    return (
      <FillScreen>
        <ErrorDisplay />
      </FillScreen>
    );
  }

  // packageInstanceIdがある場合はrequestId取得中
  if (searchParams.packageInstanceId && isConverting) {
    return <ViewportLoader />;
  }

  // requestIdがある場合は通常表示
  if (searchParams.requestId) {
    const dataSearchParams = {
      patternId: searchParams.patternId,
      requestId: searchParams.requestId,
      pageIndex: searchParams.pageIndex,
      pageSize: searchParams.pageSize,
      sortColumn: searchParams.sortColumn,
      sortOrder: searchParams.sortOrder,
      freeText: searchParams.freeText,
      deviceGenres: searchParams.deviceGenres,
      assigned: searchParams.assigned,
      reportStatus: searchParams.reportStatus,
      hasError: searchParams.hasError,
      statusFilter: searchParams.statusFilter,
      typeFilter: searchParams.typeFilter,
    };

    return (
      <FillScreen>
        <HStack className="h-full overflow-hidden">
          <DeviceInventoryOperationData
            patternId={searchParams.patternId}
            requestId={searchParams.requestId}
            searchParams={dataSearchParams}
            onSearch={handleSearch}
          />
        </HStack>
      </FillScreen>
    );
  }
}
